<template>
  <app-page labelledby="chart-title" full-width>
    <page-header
      title-id="chart-title"
      title="План счетов"
      description="Иерархия счетов и остатки за выбранный период."
    >
      <template #metadata>
        <span data-testid="chart-period">Период: {{ periodLabel }}</span>
      </template>
      <template #actions>
        <date-range-filter v-model="period" label="Период" @apply="applyPeriod" />
        <report-artifact-actions
          :payload="artifactPayload"
          :org-id="connection.org_id"
          :disabled="loading || Boolean(error)"
          neutral
        />
        <q-btn
          v-if="canEditFinance"
          outline
          no-caps
          icon="add"
          label="Добавить счет"
          @click="createOpen = true"
        />
      </template>
    </page-header>

    <page-section title="Счета">
      <app-data-table
        title="Таблица плана счетов"
        :count="visibleRows.length"
        :rows="visibleRows"
        :columns="columns"
        :pagination="pagination"
        :show-count="false"
        :show-title="false"
        hide-bottom
        :state="tableState"
        :state-title="stateTitle"
        :state-message="stateMessage"
        storage-key="finance-chart-of-accounts"
        data-testid="chart-of-accounts"
        @retry="load"
        @reset="resetFilters"
      >
        <template #filters>
          <filter-bar
            v-model="search"
            search-label="Код, название или описание"
            search-test-id="chart-search"
            :has-filters="Boolean(search || activeOnly)"
            @reset="resetFilters"
          >
            <template #quick>
              <q-toggle v-model="activeOnly" label="Только с движением" data-testid="chart-active-only" />
              <q-btn flat no-caps label="Развернуть все" @click="expandAll" />
              <q-btn flat no-caps label="Свернуть все" @click="expanded = new Set()" />
            </template>
            <template #advanced>
              <q-toggle v-model="activeOnly" label="Только с движением" />
            </template>
          </filter-bar>
        </template>
        <template #header="props">
          <q-tr :props="props" class="chart-header chart-header--groups">
            <q-th rowspan="2" auto-width aria-label="Раскрытие субсчетов" />
            <q-th rowspan="2" class="text-left">Счет</q-th>
            <q-th rowspan="2" class="text-left">Описание</q-th>
            <q-th colspan="2">Сальдо на начало периода</q-th>
            <q-th colspan="2">Обороты за период</q-th>
            <q-th colspan="2">Сальдо на конец периода</q-th>
            <q-th rowspan="2" aria-label="Действия" />
          </q-tr>
          <q-tr :props="props" class="chart-header chart-header--columns">
            <q-th>Дебет</q-th>
            <q-th>Кредит</q-th>
            <q-th>Дебет</q-th>
            <q-th>Кредит</q-th>
            <q-th>Дебет</q-th>
            <q-th>Кредит</q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr :props="props" :data-account-level="props.row.level">
            <q-td auto-width>
              <q-btn
                v-if="props.row.has_children && !search"
                flat
                dense
                size="sm"
                :icon="props.row.is_expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                :aria-label="`${props.row.is_expanded ? 'Свернуть' : 'Развернуть'} счет ${props.row.name}`"
                :aria-expanded="props.row.is_expanded ? 'true' : 'false'"
                @click.stop="toggleAccount(props.row)"
              />
              <span v-else class="account-expand-spacer" aria-hidden="true" />
            </q-td>
            <q-td class="text-left">
              <strong class="account-code">
                {{ props.row.name || '—' }}
              </strong>
            </q-td>
            <q-td class="text-left account-description">{{ props.row.description || props.row.full_name || '—' }}</q-td>
            <q-td v-for="column in amountColumns" :key="column" class="text-right">
              <amount-cell :value="props.row[column]" />
            </q-td>
            <q-td class="text-right account-actions">
              <q-btn-dropdown
                flat
                dense
                label="Открыть"
                :aria-label="`Действия со счетом ${props.row.name}`"
              >
                <q-list>
                  <q-item v-close-popup clickable :to="{ name: 'bill_card', params: { id: props.row.id } }">
                    <q-item-section><q-item-label>Карточка счета</q-item-label></q-item-section>
                  </q-item>
                  <q-item v-close-popup clickable :to="{ name: 'analytics_trial_balance', params: { id: props.row.id } }">
                    <q-item-section><q-item-label>Оборотно-сальдовая ведомость</q-item-label></q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-td>
          </q-tr>
        </template>
        <template #bottom-row>
          <q-tr class="chart-total-row" data-testid="chart-summary">
            <q-td colspan="2" />
            <q-td class="text-left text-bold">Итого</q-td>
            <q-td v-for="column in amountColumns" :key="column" class="text-right text-bold">
              <amount-cell :value="summary[column]" />
            </q-td>
            <q-td />
          </q-tr>
        </template>
      </app-data-table>
    </page-section>

    <create-new-bill v-if="createOpen" @onClose="createOpen = false" />
  </app-page>
</template>

<script>
import AppDataTable from '../../components/ui/AppDataTable.vue'
import AmountCell from '../../components/ui/AmountCell.vue'
import AppPage from '../../components/ui/AppPage.vue'
import DateRangeFilter from '../../components/ui/DateRangeFilter.vue'
import FilterBar from '../../components/ui/FilterBar.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'
import CreateNewBill from '../../components/bills/create_new_bill.vue'
import ReportArtifactActions from '../../components/documents/ReportArtifactActions.vue'
import { formatDate, toIsoDate, unixEndOfDay, unixStartOfDay } from '../../services/dateFormat'
import {
  EMPTY_ACCOUNT_SUMMARY,
  accountHasMovement,
  flattenAccounts,
  flattenAllAccounts,
  normalizeBillsResponse
} from '../../services/financeCore'

const amountColumns = Object.freeze(['debet_start', 'credit_start', 'debet_period', 'credit_period', 'debet_end', 'credit_end'])
const columns = Object.freeze([
  { name: 'expand', label: '', field: 'expand', align: 'left', required: true },
  { name: 'name', label: 'Счет', field: 'name', align: 'left', required: true },
  { name: 'description', label: 'Описание', field: 'description', align: 'left', required: true },
  { name: 'debet_start', label: 'Дебет', field: 'debet_start', align: 'right', required: true },
  { name: 'credit_start', label: 'Кредит', field: 'credit_start', align: 'right', required: true },
  { name: 'debet_period', label: 'Дебет', field: 'debet_period', align: 'right', required: true },
  { name: 'credit_period', label: 'Кредит', field: 'credit_period', align: 'right', required: true },
  { name: 'debet_end', label: 'Дебет', field: 'debet_end', align: 'right', required: true },
  { name: 'credit_end', label: 'Кредит', field: 'credit_end', align: 'right', required: true },
  { name: 'actions', label: 'Действия', field: 'actions', align: 'right', required: true }
])

export default {
  name: 'BillsPlan',
  components: { AppDataTable, AmountCell, AppPage, CreateNewBill, DateRangeFilter, FilterBar, PageHeader, PageSection, ReportArtifactActions },
  data() {
    const stored = this.$store.state.filter_period
    return {
      columns,
      amountColumns,
      accounts: [],
      summary: { ...EMPTY_ACCOUNT_SUMMARY },
      loading: true,
      error: '',
      errorState: 'error',
      search: String(this.$route.query.search || ''),
      activeOnly: this.$route.query.active === '1',
      expanded: new Set(),
      period: { from: toIsoDate(stored.from), to: toIsoDate(stored.to) },
      pagination: { page: 1, rowsPerPage: 0, sortBy: null, descending: false },
      createOpen: false
    }
  },
  computed: {
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    periodLabel() {
      return `${formatDate(this.period.from, '—')} — ${formatDate(this.period.to, '—')}`
    },
    artifactPayload() {
      return {
        org_id: this.connection.org_id,
        report_code: 'chart_of_accounts',
        period_start: this.period.from,
        period_end: this.period.to,
        date: this.period.to,
        unit: 'rub',
        currency: 'RUB'
      }
    },
    visibleRows() {
      const query = this.search.trim().toLocaleLowerCase('ru-RU')
      const source = query ? flattenAllAccounts(this.accounts) : flattenAccounts(this.accounts, this.expanded)
      return source.filter((row) => {
        const matchesQuery = !query || [row.name, row.full_name, row.description]
          .some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(query))
        return matchesQuery && (!this.activeOnly || accountHasMovement(row))
      })
    },
    tableState() {
      if (this.loading) return 'loading'
      if (this.error) return this.errorState === 'access-denied' ? 'access-denied' : 'error'
      if (!this.accounts.length) return 'empty'
      if (!this.visibleRows.length) return 'filtered-empty'
      return 'ready'
    },
    stateTitle() {
      return this.error ? 'Не удалось загрузить план счетов' : 'План счетов пуст'
    },
    stateMessage() {
      return this.error || 'Для организации еще не настроены счета.'
    }
  },
  watch: {
    search: 'syncQuery',
    activeOnly: 'syncQuery'
  },
  created() {
    this.emitter.on('didReceiveBillsList', this.onBills)
    this.load()
  },
  beforeUnmount() {
    this.emitter.off('didReceiveBillsList', this.onBills)
  },
  methods: {
    accountHasMovement,
    load() {
      this.loading = true
      this.error = ''
      const request = () => this.connection.getBillsList('chart-of-accounts', unixStartOfDay(this.period.from), unixEndOfDay(this.period.to))
      if (this.connection.isAuthenticated) request()
      else {
        const ready = () => {
          this.emitter.off('connectionReady', ready)
          request()
        }
        this.emitter.on('connectionReady', ready)
      }
    },
    onBills(response) {
      const result = normalizeBillsResponse(response)
      this.loading = false
      if (!result.ok) {
        this.accounts = []
        this.error = result.message
        this.errorState = String(response && response.error).includes('access_denied') ? 'access-denied' : result.state
        return
      }
      this.accounts = result.bills
      this.summary = result.summary
      this.error = ''
      this.expanded = new Set()
    },
    applyPeriod(value) {
      this.period = { ...value }
      this.$store.commit('updateFilterPeriod', value)
      this.load()
    },
    toggleAccount(row) {
      const next = new Set(this.expanded)
      const id = String(row.id)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      this.expanded = next
    },
    expandAll() {
      this.expanded = new Set(flattenAllAccounts(this.accounts).filter((row) => row.has_children).map((row) => String(row.id)))
    },
    resetFilters() {
      this.search = ''
      this.activeOnly = false
    },
    syncQuery() {
      const query = { ...this.$route.query }
      if (this.search) query.search = this.search
      else delete query.search
      if (this.activeOnly) query.active = '1'
      else delete query.active
      this.$router.replace({ query })
    }
  }
}
</script>

<style scoped>
.account-expand-spacer { display: inline-block; width: 2rem; }
.account-code { display: block; white-space: nowrap; }
.account-description { min-width: 16rem; max-width: 24rem; overflow-wrap: anywhere; }
.account-actions { white-space: nowrap; }
.chart-header th, .chart-total-row td { border-color: var(--sid-color-line-strong); }
.chart-header--groups th { top: 0 !important; text-align: center; }
.chart-header--groups th.text-left { text-align: left; }
.chart-header--columns th { top: var(--sid-row-compact) !important; text-align: center; }
.chart-total-row { font-weight: var(--sid-font-weight-semibold); }
</style>
